import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, ChevronDown, GlobeIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { useLanguage } from '../../context/LanguageContext';
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const isIS = language === 'is';
  const serviceLinks = [{
    name: t('navbar.servicesDropdown.overview'),
    path: '/services'
  }, {
    name: isIS ? 'Einstaklingsþjálfun' : '1:1 Coaching',
    path: '/services/coaching'
  }, {
    name: isIS ? 'Innbyggður sérfræðingur' : 'Embedded Specialist',
    path: '/services/embedded-partner'
  }, {
    name: t('navbar.servicesDropdown.courses'),
    path: '/services/courses'
  }, {
    name: t('navbar.servicesDropdown.workshops'),
    path: '/services/workshops'
  }, {
    name: t('navbar.servicesDropdown.talks'),
    path: '/services/talks'
  }];
  const isActive = (path: string) => location.pathname === path;
  const isServicesActive = () => location.pathname.startsWith('/services');
  const isEventsActive = () => location.pathname === '/events';
  const isWritingActive = () => location.pathname.startsWith('/writing');
  const toggleLanguage = () => setLanguage(language === 'is' ? 'en' : 'is');
  return <nav className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-md border-b border-gray-100" data-id="element-123" data-mp="2nzg0q7z3cvv7">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-124" data-mp="2jxb91es7e9b7">
        <div className="flex justify-between items-center h-20" data-id="element-125" data-mp="2ez03hg8xykys">
          <Link to="/" className="flex items-center gap-2 group" data-id="element-126" data-mp="2f0uo0tgawb2c">
            <span className="font-signature text-3xl text-teal" data-id="element-127" data-mp="2a06qdoxkgvz5">HelgiG</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8" data-id="element-128" data-mp="251diqd4ynz6w">
            <Link to="/" className={`relative font-medium transition-colors hover:text-coral ${isActive('/') ? 'text-coral' : 'text-gray-600'}`} data-id="element-129" data-mp="1vcyhwvvn74qv">
              {t('navbar.home')}
              {isActive('/') && <span className="absolute -bottom-1 left-0 right-0 h-1 bg-coral rounded-full" data-id="element-130" data-mp="1mecscgrr6pgl" />}
            </Link>

            <Link to="/about" className={`relative font-medium transition-colors hover:text-coral ${isActive('/about') ? 'text-coral' : 'text-gray-600'}`} data-id="element-131" data-mp="1qg7t9k5rv0tu">
              {t('navbar.about')}
              {isActive('/about') && <span className="absolute -bottom-1 left-0 right-0 h-1 bg-coral rounded-full" data-id="element-132" data-mp="25acnu1jxwb4x" />}
            </Link>

            {/* Services Dropdown */}
            <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)} data-id="element-133" data-mp="29ams99s32tho">
              <Link to="/services" className={`relative font-medium transition-colors hover:text-coral flex items-center gap-1 ${isServicesActive() ? 'text-coral' : 'text-gray-600'}`} data-id="element-134" data-mp="31ulesid1z46p">
                {t('navbar.services')}
                <ChevronDown size={16} className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} data-id="element-135" data-mp="102t9plk9jal2" />
                {isServicesActive() && <span className="absolute -bottom-1 left-0 right-0 h-1 bg-coral rounded-full" data-id="element-136" data-mp="7fgxo5bqal60" />}
              </Link>

              <AnimatePresence data-id="element-137" data-mp="2gvwo3onjxgi">
                {servicesOpen && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 8
              }} transition={{
                duration: 0.15
              }} className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 overflow-hidden" data-id="element-138" data-mp="2iqirlbntvm6">
                    {serviceLinks.map((link, i) => <React.Fragment key={link.path} data-id="element-139" data-mp="3tj118gpiz0xz" data-mpi={i}>
                        {i === 3 && <div className="border-t border-gray-100 my-1" data-id="element-140" data-mp="2beqk44e1y7m" />}
                        <Link to={link.path} className={`block px-4 py-2.5 text-sm font-medium transition-colors ${isActive(link.path) ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50 hover:text-coral'}`} data-id="element-141" data-mp="2dskuar8v6e4">
                          {link.name}
                        </Link>
                      </React.Fragment>)}
                  </motion.div>}
              </AnimatePresence>
            </div>

            <Link to="/events" className={`relative font-medium transition-colors hover:text-coral ${isEventsActive() ? 'text-coral' : 'text-gray-600'}`} data-id="element-142" data-mp="u2ukkj89yn7v">
              {isIS ? 'Viðburðir' : 'Events'}
              {isEventsActive() && <span className="absolute -bottom-1 left-0 right-0 h-1 bg-coral rounded-full" data-id="element-143" data-mp="141bgzvpzfu1b" />}
            </Link>

            {/* Writing */}
            <Link to="/writing" className={`relative font-medium transition-colors hover:text-coral ${isWritingActive() ? 'text-coral' : 'text-gray-600'}`} data-id="element-144" data-mp="1965gi6sc7es3">
              {isIS ? 'Skrif' : 'Writing'}
              {isWritingActive() && <span className="absolute -bottom-1 left-0 right-0 h-1 bg-coral rounded-full" data-id="element-145" data-mp="1070n1evmubms" />}
            </Link>

            <Link to="/contact" className={`relative font-medium transition-colors hover:text-coral ${isActive('/contact') ? 'text-coral' : 'text-gray-600'}`} data-id="element-146" data-mp="1wxavkjqge31c">
              {t('navbar.contact')}
              {isActive('/contact') && <span className="absolute -bottom-1 left-0 right-0 h-1 bg-coral rounded-full" data-id="element-147" data-mp="1j3a3u9xx5gah" />}
            </Link>

            <button onClick={toggleLanguage} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-sm font-medium text-gray-600 hover:border-teal hover:text-teal transition-colors" aria-label={language === 'is' ? 'Switch to English' : 'Skipta yfir á íslensku'} data-id="element-148" data-mp="2fn93mzu93w4z">
              <GlobeIcon size={16} data-id="element-149" data-mp="3vdtgqynudkl2" />
              <span data-id="element-150" data-mp="3vcqrswa2xs7s">{language === 'is' ? 'EN' : 'IS'}</span>
            </button>

            <Link to="/contact" data-id="element-151" data-mp="45xl5cwip0vv">
              <Button size="sm" variant="secondary" data-id="element-152" data-mp="44w6kygalw9e">{t('navbar.bookNow')}</Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-3" data-id="element-153" data-mp="3rbyi61xaplaz">
            <button onClick={toggleLanguage} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-gray-200 text-sm font-medium text-gray-600 hover:border-teal hover:text-teal transition-colors" aria-label={language === 'is' ? 'Switch to English' : 'Skipta yfir á íslensku'} data-id="element-154" data-mp="3ceflmdedm3sz">
              <GlobeIcon size={14} data-id="element-155" data-mp="1fkuhz8yy0uds" />
              <span data-id="element-156" data-mp="1fjrulb0txa2m">{language === 'is' ? 'EN' : 'IS'}</span>
            </button>
            <button onClick={() => setIsOpen(!isOpen)} className="text-gray-600 hover:text-coral transition-colors" data-id="element-157" data-mp="304g6h5tj808p">
              {isOpen ? <X size={28} data-id="element-158" data-mp="39tzrah0t2467" /> : <Menu size={28} data-id="element-159" data-mp="39uhxa5d9rwrb" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence data-id="element-160" data-mp="35289ovzmn8s7">
        {isOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-white border-b border-gray-100 overflow-hidden" data-id="element-161" data-mp="351ge3l615b1x">
            <div className="px-4 pt-2 pb-6 space-y-2" data-id="element-162" data-mp="3srizjiffvirk">
              <Link to="/" onClick={() => setIsOpen(false)} className={`block px-4 py-3 rounded-xl font-medium ${isActive('/') ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-163" data-mp="3nu0s5wh2lblo">
                {t('navbar.home')}
              </Link>
              <Link to="/about" onClick={() => setIsOpen(false)} className={`block px-4 py-3 rounded-xl font-medium ${isActive('/about') ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-164" data-mp="1thaynfum64et">
                {t('navbar.about')}
              </Link>

              <div data-id="element-165" data-mp="1hbu92nt7ly1x">
                <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium ${isServicesActive() ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-166" data-mp="1hdf4sgr9fn1d">
                  {t('navbar.services')}
                  <ChevronDown size={18} className={`transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} data-id="element-167" data-mp="ylvzu4dodywp" />
                </button>
                {mobileServicesOpen && <div className="pl-4 pt-1 space-y-1" data-id="element-168" data-mp="2a11gydejzzzu">
                    {serviceLinks.map((link, i) => <React.Fragment key={link.path} data-id="element-169" data-mp="2ezvylc0sbiid" data-mpi={i}>
                        {i === 3 && <div className="border-t border-gray-100 my-1" data-id="element-170" data-mp="2f28ms6x4yp3v" />}
                        <Link to={link.path} onClick={() => {
                  setIsOpen(false);
                  setMobileServicesOpen(false);
                }} className={`block px-4 py-2.5 rounded-lg text-sm font-medium ${isActive(link.path) ? 'bg-coral/5 text-coral' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`} data-id="element-171" data-mp="3bhe45yftq23">
                          {link.name}
                        </Link>
                      </React.Fragment>)}
                  </div>}
              </div>

              <Link to="/events" onClick={() => setIsOpen(false)} className={`block px-4 py-3 rounded-xl font-medium ${isEventsActive() ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-172" data-mp="36spqwbeb5zpy">
                {isIS ? 'Viðburðir' : 'Events'}
              </Link>

              <Link to="/writing" onClick={() => setIsOpen(false)} className={`block px-4 py-3 rounded-xl font-medium ${isWritingActive() ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-173" data-mp="103kz56qjyvwk">
                {isIS ? 'Skrif' : 'Writing'}
              </Link>

              <Link to="/contact" onClick={() => setIsOpen(false)} className={`block px-4 py-3 rounded-xl font-medium ${isActive('/contact') ? 'bg-coral/10 text-coral' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-174" data-mp="28qc2lgl8bpk7">
                {t('navbar.contact')}
              </Link>

              <div className="pt-2 px-4" data-id="element-175" data-mp="1g3hkgqbvx29w">
                <Link to="/contact" onClick={() => setIsOpen(false)} data-id="element-176" data-mp="1tst61s2b8827">
                  <Button className="w-full" variant="secondary" data-id="element-177" data-mp="1tv5w6si81oqc">{t('navbar.bookNow')}</Button>
                </Link>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}